
const localStorageKey = "olav-customer-access-token";
const sessionStorageKey = "olav-customer-";

const getAccessToken = () => {
  if (window.localStorage[localStorageKey]) {
    const token = JSON.parse(window.localStorage[localStorageKey]);

    if (new Date() < new Date(token.expiresAt)) {
      return token.accessToken;
    }
  }

  return null;
};

const shopifyQuery = (query, variables) => {
  return fetch(`https://${process.env.GATSBY_SHOPIFY_DOMAIN}/api/2023-04/graphql.json`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  })
    .then((res) => res.json())
    .catch((res) => console.error(res));
};

export const withAccessToken = (callback) => {
  const token = getAccessToken();

  if (token === null) return new Promise((resolve) => resolve(null));

  return callback(token);
};

export const customerExist = async (email) => {
  const response = await fetch("/.netlify/functions/customer-exist",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    }
  );

  return response.json();
};

export const createCustomer = ({ firstName, lastName, email, password }) => {
  return shopifyQuery(`
      mutation customerCreate($input: CustomerCreateInput!) {
        customerCreate(input: $input) {
          customer {
            id
          }
          customerUserErrors {
            code
            field
            message
          }
        }
      }
    `, {
    input: {
      email,
      firstName,
      lastName,
      password,
    }
  });
};

export const createCustomerWithTags = async (customer) => {
  const response = await fetch("/.netlify/functions/create-customer",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(customer)
    }
  );
  return response.json();
};

export const customerActivateByUrl = ({ activationUrl, password }) => {
  return shopifyQuery(`mutation customerActivateByUrl($activationUrl: URL!, $password: String!) {
    customerActivateByUrl(activationUrl: $activationUrl, password: $password) {
      customer {
        id
      }
      customerAccessToken {
        accessToken
        expiresAt
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }`, {
    activationUrl,
    password
  });
};

export const customerRecover = ({ email }) => {
  return shopifyQuery(`mutation customerRecover($email: String!) {
    customerRecover(email: $email) {
      customerUserErrors {
        code
        field
        message
      }
    }
  }`, { email });
};

export const customerResetByUrl = ({ resetUrl, password }) => {
  return shopifyQuery(`mutation customerResetByUrl($resetUrl: URL!, $password: String!) {
    customerResetByUrl(resetUrl: $resetUrl, password: $password) {
      customer {
        id
      }
      customerAccessToken {
        accessToken
        expiresAt
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }`, {
    resetUrl,
    password
  });
};




export const updateCustomer = (customer) => {
  return withAccessToken((token) => {
    return shopifyQuery(`
    mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
      customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
        customer {
          id
        }
        customerAccessToken {
          accessToken
          expiresAt
        }
        customerUserErrors {
          code
          field
          message
        }
      }
    }
  `, {
      customerAccessToken: token, customer
    });
  }).then((response) => {
    if (response.data?.customerUpdate?.customerAccessToken) {
      setCustomerAccessToken(response.data?.customerUpdate?.customerAccessToken);
      window.sessionStorage.removeItem(sessionStorageKey);
    }

    return response;
  });
};

export const customerSmsMarketingConsentUpdate = (customerId, telephoneActivity) => {
  return fetch("/.netlify/functions/update-sms-consent",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customerId,
        telephoneActivity
      })
    }
  );
};

export const customerDefaultAddressUpdate = (customer) => {
  let { addressId, address } = customer;

  //If a default address does not exist, create a new one one, otherwise edit the default address.
  if (!addressId) {
    return withAccessToken((token) => {
      return shopifyQuery(`
      mutation customerAddressCreate($customerAccessToken: String!, $address: MailingAddressInput!) {
        customerAddressCreate(
          customerAccessToken: $customerAccessToken
          address: $address
        ) {
          customerAddress {
            id
          }
          customerUserErrors {
            code
            field
            message
          }
        }
      }
    `, {
        customerAccessToken: token,
        address: address
      });
    }).then((response) => {
      if (response.data?.customerUpdate?.customerAccessToken) {
        setCustomerAccessToken(response.data?.customerUpdate?.customerAccessToken);
        window.sessionStorage.removeItem(sessionStorageKey);
      }

      return response;
    });
  } else {
    return withAccessToken((token) => {
      return shopifyQuery(`
      mutation customerAddressUpdate($customerAccessToken: String!, $id: ID!, $address: MailingAddressInput!) {
        customerAddressUpdate(
          customerAccessToken: $customerAccessToken
          id: $id
          address: $address
        ) {
          customerAddress {
            id
          }
          customerUserErrors {
            code
            field
            message
          }
        }
      }
    `, {
        customerAccessToken: token,
        id: addressId,
        address: address
      });
    }).then((response) => {
      if (response.data?.customerUpdate?.customerAccessToken) {
        setCustomerAccessToken(response.data?.customerUpdate?.customerAccessToken);
        window.sessionStorage.removeItem(sessionStorageKey);
      }

      return response;
    });
  }
};



export const createCustomerAccessToken = ({ email, password }) => {
  return shopifyQuery(`
      mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
        customerAccessTokenCreate(input: $input) {
          customerAccessToken {
            accessToken
            expiresAt
          }
          customerUserErrors {
            code
            field
            message
          }
        }
      }
    `, {
    input: {
      email, password
    }
  });
};

export const associateCheckout = (checkoutId) => {
  return withAccessToken((token) => {
    const storageKey = `${checkoutId}${token}`;
    if (sessionStorage.getItem(storageKey)) return;

    shopifyQuery(`
      mutation checkoutCustomerAssociateV2($checkoutId: ID!, $customerAccessToken: String!) {
        checkoutCustomerAssociateV2(
          checkoutId: $checkoutId
          customerAccessToken: $customerAccessToken
        ) {
          checkout {
            id
          }
          checkoutUserErrors {
            code
            field
            message
          }
          customer {
            id
          }
        }
      }
    `,
    {
      checkoutId,
      customerAccessToken: token
    }).then((response) => {
      if (response.data?.checkoutCustomerAssociateV2?.checkoutUserErrors?.length === 0) {
        sessionStorage.setItem(storageKey, true);
      }
    });
  });
};

export const setCustomerAccessToken = (token) => {
  window.localStorage.setItem(
    localStorageKey,
    token !== null ? JSON.stringify(token) : ""
  );
};

export const getCustomer = () =>
  withAccessToken((token) => {
    if (sessionStorage.getItem(sessionStorageKey)) {
      return new Promise((resolve) => {
        resolve(JSON.parse(sessionStorage.getItem(sessionStorageKey)));
      });
    }

    return shopifyQuery(`
      {
        customer(customerAccessToken: "${token}") {
          displayName
          firstName
          email
          id
          lastName
        }
      }
    `
    ).then((response) => {
      if (response?.data?.customer) {
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(response?.data?.customer));

        return response?.data?.customer;
      }

      return null;
    });

  }
  );

export const getPersonalInformation = () =>
  withAccessToken((token) =>
    shopifyQuery(`
    {
      customer(customerAccessToken: "${token}") {
        addresses(first: 1) {
          edges {
            node {
              id
              address1
              address2
              city
              country
              name
              province
              zip
            }
          }
        }
        acceptsMarketing
        displayName
        email
        firstName
        id
        lastName
        phone
        updatedAt
      }
    }
    `)
  );

export const getOrders = (props = {}) => {
  const afterCursorEl = props.afterCursor ? `after: "${props.afterCursor},"` : "";

  return withAccessToken((token) => shopifyQuery(`
    {
      customer(customerAccessToken: "${token}") {
        orders(first: 5, ${afterCursorEl} reverse: true) {
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          edges {
            cursor
            node {
              id
              orderNumber
              processedAt
              fulfillmentStatus
              totalPriceV2 {
                amount
                currencyCode
              }
              totalShippingPriceV2 {
                amount
                currencyCode
              }
              totalTaxV2 {
                amount
                currencyCode
              }
              totalRefundedV2 {
                amount
                currencyCode
              }
              subtotalPriceV2 {
                amount
                currencyCode
              }
              lineItems(first: 100) {
                edges {
                  node {
                    quantity
                    discountedTotalPrice {
                      amount
                      currencyCode
                    }
                    originalTotalPrice {
                      amount
                      currencyCode
                    }
                    variant {
                      id
                      sku
                      image {
                        altText
                        originalSrc
                        src
                      }
                      title
                      priceV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
              successfulFulfillments(first: 10) {
                fulfillmentLineItems {
                  edges {
                    node {
                      lineItem {
                        title
                        quantity
                        currentQuantity
                      }
                    }
                  }
                }
                trackingInfo(first: 10) {
                  number
                  url
                }
                trackingCompany
              }
            }
          }
        }
      }
    }
  `));
};

export const getOrderByEmail = async ({ email, orderNumber }) => {
  const renameKey = (obj, oldKey, newKey) => {
    obj[newKey] = obj[oldKey];
    delete obj[oldKey];
  };

  const response =
    await fetch(`https://europe-west3-myolav.cloudfunctions.net/getOrderDetails?customerEmail=${email}&orderNumber=${orderNumber}`)
      .then((res) => res.json())
      .catch(() => {
        return false;
      });

  if (!response) return null;

  const order = response.order.node;
  order.orderNumber = orderNumber.replace("OL", "");
  order.summary = response.alaikoOrder;
  order?.lineItems?.edges?.forEach((lineItem) => {
    lineItem.node.discountedTotalPrice = lineItem.node?.discountedTotalSet?.presentmentMoney;
    delete lineItem.node.discountedTotalSet;
  });
  renameKey(order, "totalPriceSet", "totalPriceV2");
  return order;
};

export const getOrderDetails = async (orderNumber) => {
  let customerId = window?.loyaltylion?.customer?.merchantId;
  let orderNr = `OL${orderNumber}`;

  const response =
    await fetch(`https://europe-west3-myolav.cloudfunctions.net/getOrder?customerId=${customerId}&orderNumber=${orderNr}`)
      .then((resp) => {
        if (resp.status === 200) return resp.json();
        else return {};
      });

  return response;
};

export const getOrderStatuses = (orderNumbers) => {
  const orders = orderNumbers.join(",");
  const response = fetch(`https://europe-west3-myolav.cloudfunctions.net/getOrderStatuses?orderNumbers=${orders}`);
  return response;
};

export const logout = () => {
  window.localStorage.removeItem(localStorageKey);
  window.sessionStorage.removeItem(sessionStorageKey);
};
